import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { generalHthHelper } from '@monorepo/helpers';
import { H2, Hr } from 'src/components/_commons';
import { hideLoadingModalReducer, setAlertResponse, setValuesAndOpenAlertModalReducer, showLoadingModalReducer } from 'src/store';
import { houseToHouseServices, hthHelper } from 'src/services';
import { hthConfigOptions } from 'src/app-config';
import { HTHNewFaceOptionsStreet } from './commons/HTHNewFaceOptionsStreet';
import { typeBlock, typeFace, typeHTHTerritory, typeHTHFace } from '@monorepo/models';
import { useDispatch } from 'react-redux';
import { useGetConfig, useGetIsDarkMode } from 'src/custom-hooks';

type typeColor = 'green' | 'red' | 'blue' | 'yellow';

const colors: typeColor[] = ['green', 'red', 'blue', 'yellow'];

type typeOption = {
    color: typeColor;
    face: typeFace | null;
    street: string;
};

type propsType = {
    hthTerritory: typeHTHTerritory;
    initBlockAddingHandler: (block: typeBlock) => void;
    isCompletingNewBlock: boolean;
    reloadHandler: () => void;
    setRunIntervals: Dispatch<SetStateAction<boolean>>;
};

export const HTHSectionMapNewBlockOptions: FC<propsType> = ({
    hthTerritory, initBlockAddingHandler, isCompletingNewBlock, reloadHandler, setRunIntervals
}) => {
    const [options, setOptions] = useState<typeOption[]>([
        { color: 'yellow', face: null, street: '' },
        { color: 'blue', face: null, street: '' },
        { color: 'red', face: null, street: '' },
        { color: 'green', face: null, street: '' }
    ]);
    const [streets, setStreets] = useState<string[]>([]);
    const config = useGetConfig();
    const dispatch = useDispatch();
    const isDarkMode = useGetIsDarkMode();
    const usedBlocks = [...new Set(hthTerritory.faces.map(x => x.block))];

    const selectBlockHandler = (block: typeBlock) => {
        setRunIntervals(true);
        initBlockAddingHandler(block);
    }

    const setOptionsHandler = (color: typeColor, face: typeFace, street: string) => {
        if (!color || !face || !street) {
            return;
        }
        setOptions(ops => ops.map(o => o.color === color ?
            {
                color,
                face,
                street
            }
            :
            o
        ));
    }

    const createNewFacesHandler = () => {  // colors
        const block = hthTerritory.faces.find(x => x.face === 'x')?.block;
        if (!block) {
            return;
        }
        dispatch(setValuesAndOpenAlertModalReducer({
            mode: 'confirm',
            title: "Confirmar",
            message: `Se van a agregar estas 4 caras correspondientes a la manzana ${generalHthHelper.MaskTheBlock(block, config.isUsingLettersForBlocks)}`,
            execution: async () => {
                const faces: typeHTHFace[] = [];
                for (const o of options) {
                    const np = hthTerritory.faces.find(p => p.face === 'x' && p.color === o.color);
                    if (!np || !o.face || !o.street) {
                        dispatch(setValuesAndOpenAlertModalReducer({
                            mode: 'alert',
                            title: "Hay algún error en los datos",
                            message: "",
                            animation: 2
                        }));
                        return;
                    }
                    const newPolygon: typeHTHFace = {
                        ...np,
                        face: o.face,
                        street: o.street
                    };
                    faces.push(newPolygon);
                }
                dispatch(showLoadingModalReducer());
                const response = await houseToHouseServices.AddPolygonFacesToTerritory(hthTerritory.territoryNumber, faces);
                dispatch(hideLoadingModalReducer());
                if (!response.success) {
                    dispatch(setAlertResponse({ response }));
                    return;
                }
                reloadHandler();
            }
        }));
    }

    useEffect(() => {
        const streets000: string[] = [];
        const streets111 = hthHelper.GetStreetsByTerritory(hthTerritory);
        streets111.forEach(x => streets000.push(x));
        houseToHouseServices.GetStreetsByTerritory(hthTerritory.territoryNumber).then(response => {
            if (response && response.data?.streets.length) {
                response.data.streets.forEach(x => {
                    if (!streets000.includes(x)) {
                        streets000.push(x);
                    }
                });
            }
            setStreets(streets000);
        });
    }, [hthTerritory]);

    return (
        <div className={`container pb-5 ${isDarkMode ? 'bg-dark' : ''}`}>
            {!hthTerritory.newBlockPolygon && !isCompletingNewBlock &&
                <>
                <h2 className={'text-center my-5'}>
                    Seleccionar la cara que se va a agregar:
                </h2>
                <Dropdown className={'d-block m-auto text-center mb-5'}>
                    <Dropdown.Toggle variant={'danger'} className={'text-center'}>
                        Seleccionar la Manzana que se va a agregar &nbsp;
                    </Dropdown.Toggle>
                    <Dropdown.Menu show className={'text-center'}>
                        <Dropdown.Header>
                            Seleccionar {config.isUsingLettersForBlocks ? 'la letra' : 'el número'}
                        </Dropdown.Header>
                        {hthConfigOptions.blocks.filter(x => !usedBlocks.includes(x)).map(b =>
                            <Dropdown.Item key={b} onClick={() => selectBlockHandler(b)}>
                                Manzana {generalHthHelper.MaskTheBlock(b, config.isUsingLettersForBlocks)}
                            </Dropdown.Item>
                        )}
                    </Dropdown.Menu>
                </Dropdown>
                </>
            }
            {isCompletingNewBlock &&
                <>
                <H2 title={`COMPLETAR MANZANA NUEVA ${generalHthHelper.MaskTheBlock(hthTerritory.faces.find(x => x.face === 'x')?.block || '1', config.isUsingLettersForBlocks)}`} />
                {colors.map(c =>
                    <NewBlockOption key={c}
                        color={c}
                        setOptionsHandler={setOptionsHandler}
                        streets={streets}
                    />
                )}
                <button className={'btn btn-general-blue d-block mx-auto mt-4 py-2'}
                    style={{ width: '400px' }}
                    onClick={() => createNewFacesHandler()}
                    disabled={!!options.filter(o => !o.face || !o.street)?.length
                        || new Set(options.map(o => o['face'])).size !== options.map(o => o['face']).length
                        || new Set(options.map(o => o['street'])).size !== options.map(o => o['street']).length
                    }
                >
                    ACEPTAR
                </button>
                </>
            }
        </div>
    );
}

type propsType1 = {
    color: typeColor;
    setOptionsHandler: (color: typeColor, face: typeFace, street: string) => void;
    streets: string[];
};

const NewBlockOption: FC<propsType1> = ({ color, setOptionsHandler, streets }) => {
    const [face, setFace] = useState<typeFace | null>(null);
    const [street, setStreet] = useState('');
    const config = useGetConfig();
    const isDarkMode = useGetIsDarkMode();

    const translateColor = (color: typeColor) => {
        if (color === 'blue') return 'Azul';
        if (color === 'green') return 'Verde';
        if (color === 'red') return 'Rojo';
        if (color === 'yellow') return 'Amarillo';
    }

    useEffect(() => {
        if (!face || !street) {
            return;
        }
        setOptionsHandler(color, face, street);
    }, [color, face, street]);

    return (
        <div className={'container my-5'} style={{ maxWidth: '600px' }}>
            <h3 className={`mb-3 ${isDarkMode ? 'text-white' : ''}`}>
                Cara {translateColor(color)}: {face ? `cara ${generalHthHelper.MaskTheFace(face, config.isUsingLettersForBlocks)}` : ''} {street ? `, calle ${street}` : ''}
            </h3>
            <Dropdown className={'d-inline me-3'}>
                <Dropdown.Toggle
                    variant={color === 'red' ? 'danger' : color === 'blue' ? 'primary' : color === 'yellow' ? 'warning' : 'success'}
                >
                    {face ? `==> Cara ${generalHthHelper.MaskTheFace(face, config.isUsingLettersForBlocks)}` : `Seleccionar la letra de la cara ${translateColor(color)}`} &nbsp;
                </Dropdown.Toggle>
                <Dropdown.Menu show={false}>
                    <Dropdown.Header> Seleccionar la letra </Dropdown.Header>
                    {hthConfigOptions.faces.map(face =>
                        <Dropdown.Item key={face} eventKey={face} onClick={() => setFace(face)}>
                            Cara {generalHthHelper.MaskTheFace(face, config.isUsingLettersForBlocks)}
                        </Dropdown.Item>
                    )}
                </Dropdown.Menu>
            </Dropdown>
            {face &&
                <Dropdown className={'d-inline'}>
                    <Dropdown.Toggle variant={color === 'red' ? 'danger' : color === 'blue' ? 'primary' : color === 'yellow' ? 'warning' : 'success'}>
                        {street ? `==> Calle ${street === 'other' ? '' : street}` : "Seleccionar la Calle"} &nbsp;
                    </Dropdown.Toggle>
                    <Dropdown.Menu show={false}>
                        <Dropdown.Header>
                            Seleccionar la calle
                        </Dropdown.Header>
                        {!!streets?.length && streets.map(s =>
                            <Dropdown.Item key={s} eventKey={s} onClick={() => setStreet(s)}>
                                Calle {s}
                            </Dropdown.Item>
                        )}
                        <Dropdown.Divider />
                        <Dropdown.Item key={'other'} eventKey={'other'} onClick={() => setStreet('other')}>
                            Otra...
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            }
            {street === 'other' &&
                <HTHNewFaceOptionsStreet
                    setSelectedStreet={setStreet}
                />
            }
            <Hr classes={'mt-5'} />
        </div>
    );
}
